import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Footer } from "./components/layout/Footer";
import { Header } from "./components/layout/Header";
import { StoreProvider } from "./context/StoreContext";
import { ThemeProvider } from "./context/ThemeContext";
import { HomePage } from "./pages/HomePage";
import { ShopPage } from "./pages/ShopPage";

const lazyPage = (importer, exportName) =>
  lazy(() =>
    importer().then((module) => ({
      default: module[exportName],
    })),
  );

const AboutPage = lazyPage(() => import("./pages/AboutPage"), "AboutPage");
const AccountPage = lazyPage(() => import("./pages/AccountPage"), "AccountPage");
const AdminStudioPage = lazyPage(() => import("./pages/AdminStudioPage"), "AdminStudioPage");
const AdminOrdersPage = lazyPage(() => import("./pages/AdminOrdersPage"), "AdminOrdersPage");
const AdminCouponsPage = lazyPage(() => import("./pages/AdminCouponsPage"), "AdminCouponsPage");
const AdminLeadsPage = lazyPage(() => import("./pages/AdminLeadsPage"), "AdminLeadsPage");
const AdminUsersPage = lazyPage(() => import("./pages/AdminUsersPage"), "AdminUsersPage");
const AdminStockPage = lazyPage(() => import("./pages/AdminStockPage"), "AdminStockPage");
const CartPage = lazyPage(() => import("./pages/CartPage"), "CartPage");
const CheckoutPage = lazyPage(() => import("./pages/CheckoutPage"), "CheckoutPage");
const CollectionPage = lazyPage(() => import("./pages/CollectionPage"), "CollectionPage");
const ContactPage = lazyPage(() => import("./pages/ContactPage"), "ContactPage");
const CustomizePage = lazyPage(() => import("./pages/CustomizePage"), "CustomizePage");
const FAQPage = lazyPage(() => import("./pages/FAQPage"), "FAQPage");
const NotFoundPage = lazyPage(() => import("./pages/NotFoundPage"), "NotFoundPage");
const PolicyPage = lazyPage(() => import("./pages/PolicyPage"), "PolicyPage");
const ProductPage = lazyPage(() => import("./pages/ProductPage"), "ProductPage");
const SearchPage = lazyPage(() => import("./pages/SearchPage"), "SearchPage");
const SizeGuidePage = lazyPage(() => import("./pages/SizeGuidePage"), "SizeGuidePage");
const TrackOrderPage = lazyPage(() => import("./pages/TrackOrderPage"), "TrackOrderPage");
const WishlistPage = lazyPage(() => import("./pages/WishlistPage"), "WishlistPage");

const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);
  return null;
};

const AppShell = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <ScrollToTop />
      <Header />
      <main className="min-h-[70vh]">
        <Suspense
          fallback={
            <div
              className="flex min-h-[40vh] items-center justify-center text-sm text-[#b8aa90]"
              data-testid="app-route-loading-state"
            >
              Loading page...
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/product/:slug" element={<ProductPage />} />
            <Route path="/collections/:collectionSlug" element={<CollectionPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/customize" element={<CustomizePage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/policies/:policyKey" element={<PolicyPage />} />
            <Route path="/size-guide" element={<SizeGuidePage />} />
            <Route path="/track-order" element={<TrackOrderPage />} />
            <Route path="/wishlist" element={<WishlistPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/admin/studio" element={<AdminStudioPage />} />
            <Route path="/admin/orders" element={<AdminOrdersPage />} />
            <Route path="/admin/coupons" element={<AdminCouponsPage />} />
            <Route path="/admin/leads" element={<AdminLeadsPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/stock" element={<AdminStockPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <Toaster richColors position="bottom-right" />
    </div>
  );
};

export default function App() {
  return (
    <ThemeProvider>
      <StoreProvider>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </StoreProvider>
    </ThemeProvider>
  );
}
